import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** * Utility function to merge class names using clsx and tailwind-merge.
 * This is useful for conditionally applying Tailwind CSS classes in a React component.
 * @param inputs - Class names to be merged.
 * @returns A single string of merged class names.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Utility function to get initials from a name.
 * This is useful for generating avatar initials or similar use cases.
 * @param name - The full name from which to extract initials.
 * @returns A string containing the initials of the name.
 */
export function getInitals(name: string): string {
  const names = name.split(" ");
  if (names.length === 0) return "";
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (
    names[0].charAt(0).toUpperCase() +
    names[names.length - 1].charAt(0).toUpperCase()
  );
}

/**
 * Utility function to get the default profile image URL.
 * @param email - Optional email to generate a unique avatar URL.
 * @returns A string containing the default profile image URL.
 */
export function defaultProfileImage({ email }: { email?: string }): string {
  return email
    ? `https://avatar.vercel.sh/${email}`
    : "https://6tzeyhmmyh.ufs.sh/f/0tp8Nw6atwboAzXu9bZ8GztK9FMVdS4RhTwQHB6CZ2mlEaNO";
}

/**
 *  Utility function to extract the name from an email address.
 * This is useful for displaying user names in a more readable format.
 * @param email - The email address from which to extract the name.
 * @returns A string containing the name extracted from the email.
 */
export function getNameFromEmail(email: string): string {
  const [name] = email.split("@");
  return name.replace(".", " ");
}

/**
 * Utility function to normalize names by trimming whitespace,
 * removing special characters, and capitalizing the first letter of each word.
 * @param name - The name to normalize.
 * @returns A string containing the normalized name.
 */
export function normalizeName(name: string) {
  return name
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Utility function to capitalize the first letter of a string.
 * @param str - The string to capitalize.
 * @returns A string with the first letter capitalized.
 */
export function capitalizeFirstLetter(str: string): string {
  if (!str) return str; // Handle empty or null strings
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Utility function to shuffle an array using the Fisher-Yates algorithm.
 * This is useful for randomizing the order of elements in an array.
 * @param array - The array to shuffle.
 * @returns A new array with the elements shuffled.
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
}

/** Utility function to get a random item from an array.
 * This is useful for selecting a random element from a list.
 * @param array - The array from which to select a random item.
 * @returns A random item from the array, or undefined if the array is empty.
 */
export function getRandomItem<T>(array: T[]): T | undefined {
  if (array.length === 0) return undefined;
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
}

/** Utility function to get a random number within a specified range.
 * This is useful for generating random integers for various purposes.
 * @param min - The minimum number (inclusive).
 * @param max - The maximum number (inclusive).
 * @returns A random integer between min and max.
 */
export function getRandomNumber(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Utility function that turns a word slug into a proper noun phrase.
 * It replaces hyphens and underscores with spaces and capitalizes the first letter of each word.
 * @param slug - The slug string to convert.
 * @returns A properly formatted noun phrase.
 */
export function slugToNounPhrase(slug: string): string {
  return slug
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Utility function to slugify a given text.
 * It converts the text to lowercase, trims whitespace, replaces spaces with hyphens,
 * removes non-word characters, and collapses multiple hyphens into a single hyphen.
 * @param text - The text to slugify.
 * @returns A slugified version of the text.
 */
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\-]+/g, "") // Remove all non-word chars
    .replace(/\-\-+/g, "-"); // Replace multiple - with single -
}

/**
 * Extract dominant colors from an image using canvas
 * Returns the two most dominant colors
 */
export async function extractDominantColors(
  imageUrl: string,
): Promise<{ primary: string; secondary: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Could not get canvas context");
        }

        // Resize for performance
        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        // Color frequency map
        const colorMap = new Map<string, number>();

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];

          // Skip transparent pixels and very dark/light pixels
          if (a < 128 || r + g + b < 30 || r + g + b > 735) {
            continue;
          }

          // Quantize colors to reduce variations
          const qR = Math.round(r / 32) * 32;
          const qG = Math.round(g / 32) * 32;
          const qB = Math.round(b / 32) * 32;

          const color = `${qR},${qG},${qB}`;
          colorMap.set(color, (colorMap.get(color) || 0) + 1);
        }

        // Get top 2 colors
        const sortedColors = Array.from(colorMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2);

        if (sortedColors.length === 0) {
          resolve({ primary: "#3b82f6", secondary: "#8b5cf6" });
          return;
        }

        // Convert string "r,g,b" to tuple [r, g, b]
        const parseRgb = (rgbStr: string): [number, number, number] => {
          const parts = rgbStr.split(",").map(Number);
          return [parts[0], parts[1], parts[2]];
        };

        const primary = rgbToHex(
          parseRgb(sortedColors[0]?.[0] || "59,130,246"),
        );
        const secondary = rgbToHex(
          parseRgb(
            sortedColors[1]?.[0] || sortedColors[0]?.[0] || "139,92,246",
          ),
        );

        resolve({ primary, secondary });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = imageUrl;
  });
}

/**
 * Converts a single RGB color component (0-255) to a two-digit hexadecimal string.
 * @param {number} c The color component value.
 * @returns {string} The two-digit hexadecimal representation.
 */
function componentToHex(c: number): string {
  const hex = c.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

/**
 * Converts RGB color values to a hexadecimal color code string.
 * @param {number} r The red component (0-255).
 * @param {number} g The green component (0-255).
 * @param {number} b The blue component (0-255).
 * @returns {string} The hexadecimal color code (e.g., "#0033ff").
 */
function rgbToHex([r, g, b]: [number, number, number]): string {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}
